import { useState } from 'react';
import { toast } from 'react-toastify';
import UseContext from 'hooks/useContext';
import useModal from 'components/modal/useModal';
import ApiDatabase from 'server';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import { dateWordShort } from 'helper/helper';

/**
 * Confirming the generation of a contract (JBWEB-1138).
 *
 * Generating is what ends the period during which the mission can be edited
 * freely: from then on every change goes through an amendment. Before the
 * planned date that is a forcing, and the modal says so; from it, it is the
 * expected action and the confirmation is a plain one.
 */
const ModalContractGeneration = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn } = UseContext();
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem('xsrfToken');

  const isDue = Boolean(modalData?.isDue);
  const generationLabel = modalData?.generationLabel;
  const pendingInterruption = modalData?.pendingInterruption ?? null;

  const confirm = () => {
    if (isLoading) return;
    setIsLoading(true);

    ApiDatabase.requestGenerationContract(
      { token, idMission: modalData?.idMission },
      () => {
        setIsLoading(false);
        toast.success('La génération du contrat a bien été effectuée');
        setModalDataReturn({ type: 'contract_generated' });
        toggle();
      },
      (err) => {
        setIsLoading(false);
        if (err.name === 'CanceledError') return;
        toast.error(
          'Une erreur est survenue lors de la génération du contrat : ' +
            (err?.response?.data?.error ??
              err?.response?.data?.message ??
              'erreur inconnue')
        );
      }
    );
  };

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        {isDue ? 'Générer le contrat' : 'Forcer la génération du contrat'}
      </Modalheader>
      <ModalBody>
        <div className={'flex flex-col gap-3'}>
          {/* The mission was interrupted while it had no contract: the
              closure has been waiting for this document. Said before the
              click, because the contract comes out already closed. */}
          {pendingInterruption && (
            <div className="border border-yellow-200 bg-yellow-50 rounded-md p-3 flex flex-col gap-1 ft-sm">
              <span className="font-semibold text-gray-800">
                Cette mission a été interrompue
              </span>
              <span className="text-gray-700">
                Le contrat sera généré sur la période initiale
                {pendingInterruption.date_start && pendingInterruption.date_end
                  ? ` (${dateWordShort(pendingInterruption.date_start)} → ${dateWordShort(
                      pendingInterruption.date_end
                    )})`
                  : ''}
                , puis clôturé sur BeSTT
                {pendingInterruption.end_date
                  ? ` au ${dateWordShort(pendingInterruption.end_date)}`
                  : ''}
                {pendingInterruption.reasonName
                  ? `, motif « ${pendingInterruption.reasonName} »`
                  : ''}
                .
              </span>
            </div>
          )}
        <div className={'text-gray-500'}>
          {isDue ? (
            // What the question is for: generating is what closes the period
            // during which the mission can still be edited freely.
            <>
              Êtes-vous sûr de générer le contrat de cette mission ? Une fois le
              contrat généré, toute modification de la mission devra passer par
              un avenant.
            </>
          ) : (
            <>
              Êtes-vous sûr de forcer la génération du contrat ? La génération
              est prévue le {generationLabel}. Tant que le contrat n'est pas
              généré, vous pouvez modifier la mission librement.
            </>
          )}
        </div>
        </div>
      </ModalBody>
      <ModalFooter>
        {/* Closing while the request is in flight would unmount this modal
            from under its own success callback. */}
        <Button
          size={'LG'}
          color={isLoading ? 'DISABLED' : 'SECONDARY'}
          onClick={isLoading ? undefined : toggle}
          disabled={isLoading}
        >
          Annuler
        </Button>
        <Button
          size={'LG'}
          color={isLoading ? 'DISABLED' : 'PRIMARY'}
          onClick={isLoading ? undefined : confirm}
          // `color` only greys the button; `disabled` is what refuses the
          // click, the keyboard and the second contract behind them.
          disabled={isLoading}
        >
          {isLoading ? 'Génération ...' : 'Confirmer'}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalContractGeneration;
